import {
	CHUNK_SIZE,
	createDurableOwnership,
	newUploadId,
	type DurableLeaseToken,
	type DurableOwnershipPort,
	type DurableUploadDatabase,
	type DurableUploadStoreName,
	type DurableUploadTransaction,
	type UploadLease,
	writerLeaseKey
} from "@uploads";
import {
	db,
	fileUploadSessionControlTables,
	forgetCurrentPageInitiatedFileUploadSession,
	getCompetingFileUploadOwnerLiveness,
	getCurrentFileUploadSessionOwnerId,
	getFileUploadDiagnosticContext,
	getFileUploadLaneIntentLiveness,
	getFileUploadOwnerIdLength,
	getFileUploadOwnerLiveness,
	getFileUploadSessionOwnerFields,
	getFileUploadSessionByUploadId,
	isValidFileUploadOwnerId,
	markCurrentPageInitiatedFileUploadSession,
	nextFileUploadCancellationGeneration,
	readFileUploadSession,
	readFileUploadSessionByUploadId,
	refreshOwnedSessionsHeartbeat,
	runWhileUploadOwnerLockIsFree,
	toComparableUploadOwnerCell,
	updateFileUploadSession,
	withIdbReopen,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { uploadFailureLogDetails } from "./uploadLogRedaction";
import { ensureAdminUploadBridgeReady } from "./adminUploadMigrationIntegration";
import { adminUploadTelemetry } from "./adminUploadTelemetry";

const claimListeners = new Set<(session: FileUploadSession) => void>();
const freshSessionClaims = new Map<string, (session: FileUploadSession) => Promise<boolean>>();

const ADMIN_UPLOAD_ORIGIN_KEY = "contact-admin";
export const ADMIN_WRITER_LEASE_TTL_MS = 2 * 60 * 1000;
/**
 * LSP-19609: the held-writer diagnostic read is awaited on the refusal path ON PURPOSE, so the holder
 * lands in the same log record as the refusal it explains — two records that have to be joined by
 * hand is how the incident's holder went unidentified. The cost is bounded here: a refused claim
 * (already the failing path) waits at most this long for the read, then logs `read-timed-out`.
 */
const HELD_WRITER_DIAGNOSTIC_TIMEOUT_MS = 750;
// Every app store a writer-fenced effect can reach. captureEvidenceManifests belongs here because
// deleteFileUploadSession (abandonSetup, complete, terminal cleanup) opens its own sub-transaction over
// it; a table the child needs but this parent omits makes Dexie throw SubTransactionError, which the
// driver's best-effort cleanup swallows and leaves an orphan session row (LSP-18592).
const ADMIN_FENCED_UPLOAD_STORES: readonly DurableUploadStoreName[] = [
	"fileUploadSessions",
	"largeFileUploadSessions",
	"fileUploadChunks",
	"fileUploadChunkData",
	"captureEvidenceManifests"
];
let durableOwnershipPromise: Promise<DurableOwnershipPort> | undefined;
let durableOwnershipOverride: DurableOwnershipPort | null | undefined;

/** Test seam only; production always uses the shared IndexedDB durable lease. */
export function setAdminDurableOwnershipForTests(ownership: DurableOwnershipPort | null | undefined): void {
	durableOwnershipOverride = ownership;
	durableOwnershipPromise = undefined;
}

function getDurableOwnership(): Promise<DurableOwnershipPort | undefined> {
	if (durableOwnershipOverride !== undefined) {
		if (!durableOwnershipOverride) return Promise.reject(new Error("Durable upload ownership is disabled in this test"));
		return Promise.resolve(durableOwnershipOverride);
	}
	// TRANSITIONAL — this is not the intended design, and it is not removable today.
	//
	// Why it sniffs MODE: this seam cannot express "durable ownership is unavailable" as a
	// distinct, typed state. `undefined` here means "no durable port", but the only way the
	// code has to decide that is to ask "is this a test build?" instead of asking a capability
	// question of the port itself. Existing Admin lease tests exercise the legacy row-CAS in
	// isolation and opt into the shared coordinator explicitly when testing cross-tab
	// durability, so removing the fork was measured at 254 failing tests.
	//
	// Do NOT read this as a sanctioned production fallback: whether durable ownership can ever
	// genuinely be unavailable in production (no IndexedDB, private mode, quota-denied
	// construction) has not been determined. Registered follow-up: the "admin upload ownership
	// seam" work — replace this MODE sniff with an explicit capability check returning a typed
	// `unavailable`, and replace the three-state global with injection at construction.
	if (import.meta.env?.MODE === "test") return Promise.resolve(undefined);
	return (durableOwnershipPromise ??= Promise.resolve(createDurableOwnership(createAdminDurableUploadDatabase())));
}

async function ensureAdminUploadWriterAdmission(): Promise<void> {
	// TRANSITIONAL — same MODE-sniff seam as getDurableOwnership above; see that comment for
	// why this is a build-mode question standing in for a capability question, and for the
	// registered "admin upload ownership seam" follow-up that removes it.
	const testRuntime =
		import.meta.env?.MODE === "test" ||
		(globalThis as typeof globalThis & { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV === "test";
	if (!testRuntime) await ensureAdminUploadBridgeReady();
}

/**
 * Maps the shared lease protocol onto Admin's upload database. The effect in
 * commitIfWriterOwns therefore joins this Dexie transaction, instead of using
 * the old separate coordination database as an authority for app-row writes.
 *
 * Exported for the LSP-19863 stall-budget wiring spec only; production reaches it through
 * getDurableOwnership.
 */
export function createAdminDurableUploadDatabase(): DurableUploadDatabase {
	return {
		transaction<T>(
			storeNames: readonly DurableUploadStoreName[],
			mode: "readonly" | "readwrite",
			callback: (transaction: DurableUploadTransaction) => Promise<T> | T
		) {
			const tables = [...new Set(storeNames)].map((storeName) => db.table(storeName));
			// LSP-19863: a writer-fenced commit that spans the chunk-data store MAY carry a chunk payload
			// (the persist effect's chunk write joins THIS transaction), so its stall budget must be the
			// payload budget, not the 10 s metadata one; the nested wrapper inside the effect defers to
			// this enclosing transaction and never races its own timer. The adapter cannot see the
			// effect, so CHUNK_SIZE is an upper bound: metadata-only fenced commits that list the store
			// (claim, orphan reset) accept the longer budget too — a wedged claim failing at 50 s instead
			// of 10 s is the cheaper error, since the 10 s close-inside-a-transaction was the bug — and
			// the stall message says the budget was an upper bound rather than a payload write.
			const payloadBytes = storeNames.includes("fileUploadChunkData") ? CHUNK_SIZE : undefined;
			return withIdbReopen(
				() =>
					db.transaction(mode === "readwrite" ? "rw" : "r", tables, async () => {
						const transaction: DurableUploadTransaction = {
							get: <Row>(storeName: DurableUploadStoreName, key: IDBValidKey) =>
								db.table(storeName).get(key as any) as Promise<Row | undefined>,
							put: async <Row>(storeName: DurableUploadStoreName, value: Row, key?: IDBValidKey) => {
								if (key === undefined) await db.table(storeName).put(value);
								else await db.table(storeName).put(value, key as any);
							},
							delete: (storeName: DurableUploadStoreName, key: IDBValidKey) => db.table(storeName).delete(key as any)
						};
						return callback(transaction);
					}),
				{ payloadBytes, payloadIsUpperBound: payloadBytes !== undefined, operationName: "durable-lease-transaction" }
			);
		}
	};
}

export function getAdminDurableOwnershipForCoordinator(): Promise<DurableOwnershipPort | undefined> {
	return getDurableOwnership();
}

// LSP-22113: which step of the fence refused, so a preserved orphan in the logs says whether the fence
// never came up, another writer's lease was still live, or ownership was lost before the commit.
export type AdminWriterFenceRefusal = "fence-unavailable" | "writer-held" | "ownership-lost-before-commit";

/**
 * Runs an app-row mutation behind Admin's durable writer owner/epoch fence.
 * The fallback exists for the legacy row-CAS unit harness, where durable ownership is intentionally
 * disabled; production failures to establish the fence fail closed and preserve the row and bytes.
 */
export async function commitAdminUploadWriterFence<T>(
	sessionId: string,
	effect: () => Promise<T>,
	fallbackEffect: () => Promise<T>,
	// Names the writer in the durable lease row so a stalled epoch can be attributed to the
	// operation that took it (orphan reset vs. ownerless reconciliation) instead of to whichever
	// caller happened to be hard-coded here first.
	ownerPurpose = "admin-orphan-reset"
): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned"; refusal: AdminWriterFenceRefusal }> {
	let durableOwnership: DurableOwnershipPort | undefined;
	try {
		durableOwnership = await getDurableOwnership();
	} catch (error) {
		console.warn("[Upload] Durable writer fence unavailable; preserving orphaned upload bytes", {
			sessionId,
			...uploadFailureLogDetails(error)
		});
		return { kind: "skipped-not-owned", refusal: "fence-unavailable" };
	}
	// TRANSITIONAL — reachable only because getDurableOwnership can answer `undefined`, which
	// today only the MODE sniff produces. It is not a designed degraded mode; see that function's
	// comment and the registered "admin upload ownership seam" follow-up.
	if (!durableOwnership) return { kind: "applied", value: await fallbackEffect() };

	const token = await durableOwnership.acquireWriter({
		originKey: ADMIN_UPLOAD_ORIGIN_KEY,
		identity: { itemId: sessionId },
		ownerId: `${ownerPurpose}:${getCurrentFileUploadSessionOwnerId()}:${newUploadId()}`,
		ttlMs: ADMIN_WRITER_LEASE_TTL_MS
	});
	if (!token) return { kind: "skipped-not-owned", refusal: "writer-held" };
	try {
		const committed = await durableOwnership.commitIfWriterOwns({
			token,
			storeNames: ADMIN_FENCED_UPLOAD_STORES,
			effect
		});
		return committed.kind === "applied" ? committed : { kind: "skipped-not-owned", refusal: "ownership-lost-before-commit" };
	} finally {
		await durableOwnership.releaseWriter(token);
	}
}

export function subscribeAdminUploadLeaseClaims(listener: (session: FileUploadSession) => void): () => void {
	claimListeners.add(listener);
	return () => claimListeners.delete(listener);
}

/**
 * Bridges the synchronous shared scope token contract to Admin's asynchronous durable writer
 * admission for a row that does not exist yet. The registration is one-shot and session-scoped;
 * existing-row claims continue through UploadLease.claim normally.
 */
export function registerAdminFreshSessionClaim(sessionId: string, claim: (session: FileUploadSession) => Promise<boolean>): () => void {
	freshSessionClaims.set(sessionId, claim);
	return () => {
		if (freshSessionClaims.get(sessionId) === claim) freshSessionClaims.delete(sessionId);
	};
}

export async function claimAdminFreshSessionLease(session: FileUploadSession): Promise<boolean | undefined> {
	const claim = freshSessionClaims.get(session.id);
	if (!claim) return undefined;
	freshSessionClaims.delete(session.id);
	return claim(session);
}

export function createAdminUploadLease(
	getCurrentOwnerId: () => string = getCurrentFileUploadSessionOwnerId,
	expectedOwnerClaim?: string | (() => string | undefined),
	purpose = "unknown"
): UploadLease<FileUploadSession> & {
	readonly ownerClaimId: string;
	/** Returns the current durable token for a session without awaiting a second read. */
	getDurableToken(sessionId: string): DurableLeaseToken | undefined;
	/** Releases only the token captured by the scope that acquired it. */
	releaseClaim(session: FileUploadSession, token?: DurableLeaseToken): Promise<void>;
	release(session: FileUploadSession): Promise<void>;
} {
	const ownerId = getCurrentOwnerId();
	const ownerClaimId = newUploadId();
	const diagnostics = getFileUploadDiagnosticContext();
	diagnostics.registerClaim(ownerClaimId, purpose);
	const describe = (session?: FileUploadSession) => diagnostics.describe(session, ownerClaimId, purpose);
	const durableTokens = new Map<string, DurableLeaseToken>();
	// Session ids this scope's own claim() marked as current-page-admitted, so releaseClaim retires
	// exactly its own marks — never a sibling scope's count for the same row.
	const admittedSessionUploadIds = new Map<string, string>();
	const durableOwnerId = `${ownerId}:${ownerClaimId}`;
	let durableOwnershipLost = false;
	const sameWriterFence = (left: DurableLeaseToken, right: DurableLeaseToken): boolean =>
		left.kind === right.kind &&
		left.originKey === right.originKey &&
		left.identity?.itemId === right.identity?.itemId &&
		left.identity?.takeId === right.identity?.takeId &&
		left.ownerId === right.ownerId &&
		left.epoch === right.epoch;
	let touchInFlight: Promise<void> | undefined;
	// The lease's own liveness signal must not be able to revoke the lease.
	//
	// `driverChunkLoop` runs Math.min(chunkConcurrency, chunkCount) workers (concurrency.ts) and admin
	// sets CHUNK_CONCURRENCY = 3, so a multi-chunk file opens 3 workers whose FIRST statement is
	// `await lease.touch(session)` -- three concurrent entrants into this one closure. Each read the
	// same token out of `durableTokens`; `renewWriter` rewrites the record's `expiresAt` while keeping
	// the epoch; and `recordMatchesToken` compares `expiresAt`. So the FIRST renewal invalidated the
	// copy the other two still held, they took `undefined` to mean "the lease is gone", and they
	// deleted the freshly renewed token the winner had just stored -- poisoning a scope that still
	// held a live lease. `owns()` then answered false on the very next line and the upload yielded at
	// `chunksCompleted: 0`. Single-chunk files opened one worker and never hit it, which is why this
	// only ever bit large files.
	//
	// N concurrent touches are ONE liveness signal, so coalescing them onto a single in-flight
	// renewal is what the epoch fence already assumed -- not a lock bolted on top of it. This also
	// covers the fire-and-forget touch the progress callback issues, which can overlap the next
	// chunk's awaited touch.
	const performTouch = async (): Promise<void> => {
		await ensureAdminUploadWriterAdmission();
		if (durableOwnershipLost) return;
		let durableRenewalFailed = false;
		let usedDurableHeartbeatFence = false;
		let durableHeartbeatMode = false;
		try {
			const durableOwnership = await getDurableOwnership();
			if (durableOwnership) {
				durableHeartbeatMode = true;
				for (const [sessionId, token] of durableTokens) {
					try {
						const renewed = await durableOwnership.renewWriter({ token, ttlMs: ADMIN_WRITER_LEASE_TTL_MS });
						if (renewed) {
							durableTokens.set(sessionId, renewed);
							const heartbeat = await durableOwnership.commitIfWriterOwns({
								token: renewed,
								storeNames: ["fileUploadSessions", "largeFileUploadSessions"],
								effect: async () => {
									const persisted = await readFileUploadSession(sessionId);
									if (
										persisted?.uploadOwnerId !== ownerId ||
										(persisted.uploadOwnerClaimId !== undefined && persisted.uploadOwnerClaimId !== ownerClaimId)
									)
										return false;
									await updateFileUploadSession(sessionId, { uploadOwnerHeartbeatAt: Date.now() });
									return true;
								}
							});
							if (heartbeat.kind !== "applied" || heartbeat.value === false) {
								adminUploadTelemetry.logWarn("[Upload] recording.fileUpload.leaseHeartbeatDeclined", {
									...describe(),
									sessionId,
									epoch: renewed.epoch,
									condition: "app-row-heartbeat-fence-declined",
									leaseAgeMs: Date.now() - renewed.acquiredAt
								});
								// KEEP the renewed token. `renewWriter` already APPLIED above, so this tab still holds
								// the writer lease — only the app-row heartbeat write declined, because a sibling lease
								// now owns the row's claim id. Deleting the token here discarded the one value that can
								// ever release that lease: `releaseWriter` is token-only (no release-by-identity), and the
								// token this scope captured at claim time is stale the moment any renewal lands. The lease
								// was therefore orphaned until ADMIN_WRITER_LEASE_TTL_MS lapsed, and for those two minutes
								// nothing could re-claim the session — the multi-chunk file left stuck on "finishing in the
								// background" while its object was already assembled in S3.
								//
								// Retaining it cannot leak ownership: `releaseWriter` compares the lease record against the
								// token exactly (durableOwnership `recordMatchesToken`), so if the record has genuinely
								// moved on the release is a no-op. `durableRenewalFailed` below still parks this lease, so
								// the token is retained for exactly one purpose: releasing what we still hold.
								durableRenewalFailed = true;
								continue;
							}
							usedDurableHeartbeatFence = true;
						} else {
							// Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures"):
							// this is the only branch that discards a writer token without saying so. Once the
							// token is gone `owns()` fails its `!token` check on the very next chunk and the
							// resume yields with no trace, and `releaseClaim` can only offer the stale captured
							// token, so the lease sits until its TTL. Both symptoms are invisible from here.
							adminUploadTelemetry.logWarn(
								"[Upload] Durable writer renewal reported the lease is no longer held; dropping its token",
								{
									...describe(),
									sessionId,
									epoch: token.epoch,
									leaseAgeMs: Date.now() - token.acquiredAt
								}
							);
							durableTokens.delete(sessionId);
							durableRenewalFailed = true;
						}
					} catch (error) {
						// Same reasoning as the declined-heartbeat branch above: a throw leaves the lease's state
						// unknown, so the pre-renewal token is the only release candidate that exists. Keeping it
						// is safe for the same reason — releaseWriter's exact-match CAS makes a release harmless if
						// this scope no longer owns the record. Only a renewal that definitively answered "you do
						// not hold this" (the `else` branch below) drops its token.
						durableRenewalFailed = true;
						adminUploadTelemetry.logWarn("[Upload] Durable writer renewal failed; preserving upload session for retry", {
							...describe(),
							sessionId,
							...uploadFailureLogDetails(error)
						});
					}
				}
			}
		} catch (error) {
			// If the durable coordinator itself is unavailable, the in-memory tokens cannot
			// authorize a legacy heartbeat. Drop them and leave the queued session untouched.
			durableTokens.clear();
			durableRenewalFailed = true;
			adminUploadTelemetry.logWarn("[Upload] Durable writer renewal unavailable; preserving upload session for retry", {
				...describe(),
				...uploadFailureLogDetails(error)
			});
		}
		if (durableRenewalFailed) {
			durableOwnershipLost = true;
			return;
		}
		if (!durableHeartbeatMode && !usedDurableHeartbeatFence) await refreshOwnedSessionsHeartbeat(ownerClaimId);
	};
	const releaseClaim = async (session: FileUploadSession, capturedToken?: DurableLeaseToken): Promise<void> => {
		const currentToken = durableTokens.get(session.id);
		// A scope must never release a replacement claim from the same tab. Epoch is the
		// durable successor fence; owner/identity are included for defensive adapter seams.
		if (capturedToken && currentToken && !sameWriterFence(currentToken, capturedToken)) return;
		// RELEASE THE LIVE TOKEN, NOT THE CAPTURED ONE.
		//
		// `releaseWriter` only applies to a lease record that matches its token EXACTLY, and
		// `recordMatchesToken` (durableOwnership.ts) compares `expiresAt` along with owner and epoch.
		// `touch()` renews this lease on every chunk (driverChunkLoop's per-chunk touch), and
		// `renewWriter` rewrites `expiresAt` while KEEPING the epoch, then stores the new token in
		// `durableTokens`. So after the very first chunk the scope's captured token carries a stale
		// `expiresAt` — and `sameWriterFence` cannot tell, because renewal does not bump the epoch it
		// fences on. Preferring the capture therefore handed `releaseWriter` a token that could never
		// match, it answered `skipped-not-owned`, and the lease stayed held for the REST OF ITS TTL:
		// every upload that moved at least one chunk leaked its writer on release. Nothing could then
		// re-claim that session — not this dialog's own retry ladder, not the background drain — so both
		// spun on `durable-writer-held` until the lease aged out (ADMIN_WRITER_LEASE_TTL_MS, 2 min).
		//
		// `durableTokens` is the live view: `touch()` writes each renewal into it and deletes the entry
		// the moment a renewal fails. So the current token is either this lease's newest valid token or
		// absent, and the capture is only a fallback for a scope that never renewed. The fence above is
		// unaffected — it still refuses to release a SUCCESSOR's claim, because a successor differs by
		// epoch, which renewal never changes.
		const token = currentToken ?? capturedToken;
		// claim() marks this row as current-page-admitted (below), and admission is what makes
		// resetUploadingSessions protect it unconditionally — before and instead of every liveness
		// signal, heartbeat included (LSP-17574, and the r2/r4 admission-identity rounds). That
		// protection is correct: while THIS page is driving the row, no sweep may take it.
		//
		// The mark was only ever cleared by a status write, and a lane that gives up mid-flight makes
		// no status write — every demote in the driver runs through the lease gate that just answered
		// false. So the mark outlived the claim: the row stayed "uploading", invisible to a drain that
		// reads `status: "pending"` only, and protected from the sweep for the life of the page.
		// Uploading a batch stranded one row per lost lease until the dialog was stuck "uploading".
		//
		// Releasing the claim is exactly the moment the protection stops being true, so retire only
		// the refcounted mark this lease earned. This does NOT demote the row — a cancelled upload must
		// stay out of `pending` (LSP-15669) — it only stops the row being exempt from the ordinary
		// liveness rules it should have been subject to all along.
		//
		// Deliberately ABOVE the no-durable-token early return below. The token is already gone on the
		// path that matters most — touch() deletes it and sets durableOwnershipLost the moment a writer
		// renewal fails — so gating the retirement on a token still being present would make it dead on
		// exactly the failure it compensates.
		const admittedUploadId = admittedSessionUploadIds.get(session.id);
		if (admittedUploadId !== undefined) {
			admittedSessionUploadIds.delete(session.id);
			forgetCurrentPageInitiatedFileUploadSession({ id: session.id, uploadId: admittedUploadId });
		}
		if (!token) return;
		if (!capturedToken || !currentToken || sameWriterFence(currentToken, capturedToken)) durableTokens.delete(session.id);
		try {
			const durableOwnership = await getDurableOwnership();
			if (!durableOwnership) return;
			const outcome = await durableOwnership.releaseWriter(token);
			adminUploadTelemetry.logInfo("recording.fileUpload.leaseReleaseObserved", {
				...describe(session),
				sessionId: session.id,
				outcome: outcome.kind,
				epoch: token.epoch,
				leaseAgeMs: Date.now() - token.acquiredAt
			});
			// Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures"): this
			// outcome used to be discarded, so a refused release left the lease held with no trace at all.
			// The only symptom was every later claim answering `durable-writer-held` for two minutes, which
			// reads as contention rather than as the leak it actually is. Name it at the source.
			if (outcome.kind !== "applied") {
				adminUploadTelemetry.logWarn("[Upload] Durable writer release did not apply to the observed token", {
					...describe(session),
					sessionId: session.id,
					uploadId: session.uploadId,
					outcome: outcome.kind,
					releasedCapturedToken: token === capturedToken,
					epoch: token.epoch
				});
			}
		} catch (error) {
			adminUploadTelemetry.logWarn("[Upload] Durable writer release failed", {
				...describe(session),
				sessionId: session.id,
				...uploadFailureLogDetails(error)
			});
		}
	};
	/**
	 * LSP-19609: who actually holds the writer lease a claim was just refused on.
	 *
	 * `durable-writer-held` used to log only the REFUSING lane's own owner id and claim id, so the one
	 * question triage needs — is the holder this tab's earlier scope, or a drain in another tab? — had
	 * to be guessed. The 2026-09-04 report guessed "the tab's own stale lease"; the holder was another
	 * tab's drain whose logs were not being forwarded. Read the record and say so.
	 *
	 * Observational only, and labelled that way: the refusal was atomic, this read is a separate
	 * transaction, so the record can have moved on in between — `observedHolder*` is what the store
	 * said a moment AFTER the refusal, never a basis for any decision. "Shares this owner id" is not
	 * "this page": a duplicated tab copies sessionStorage and therefore this owner id. Bounded and
	 * caught so a slow or broken store cannot turn a refused claim into a hang or a rejection.
	 */
	const describeHeldWriterLease = async (session: FileUploadSession): Promise<Record<string, unknown>> => {
		try {
			const record = (await Promise.race([
				db.table("leases").get(writerLeaseKey(ADMIN_UPLOAD_ORIGIN_KEY, { itemId: session.id })),
				new Promise<"timed-out">((resolve) => globalThis.setTimeout(() => resolve("timed-out"), HELD_WRITER_DIAGNOSTIC_TIMEOUT_MS))
			])) as { ownerId?: unknown; epoch?: unknown; expiresAt?: unknown; acquiredAt?: unknown } | "timed-out" | undefined;
			if (record === "timed-out") return { observedHolder: "read-timed-out" };
			if (!record) return { observedHolder: "no-record-visible" };
			// Persisted scalars are validated and capped before they are forwarded: a corrupt record must
			// not turn a diagnostic into an unbounded or non-scalar payload.
			const holderParts = typeof record.ownerId === "string" ? record.ownerId.split(":") : [];
			const observedHolderOwnerId = isValidFileUploadOwnerId(holderParts.at(-2)) ? holderParts.at(-2) : undefined;
			const observedHolderClaimId = isValidFileUploadOwnerId(holderParts.at(-1)) ? holderParts.at(-1) : undefined;
			return {
				...diagnostics.describe(
					{ ...(session.uploadOwnerClaimId === observedHolderClaimId ? session : {}), uploadOwnerClaimId: observedHolderClaimId },
					ownerClaimId,
					purpose
				),
				observedHolderOwnerId,
				observedHolderClaimId,
				observedHolderLeaseAgeMs: typeof record.acquiredAt === "number" ? Date.now() - record.acquiredAt : undefined,
				observedHolderEpoch: typeof record.epoch === "number" ? record.epoch : undefined,
				observedHolderExpiresInMs: typeof record.expiresAt === "number" ? record.expiresAt - Date.now() : undefined,
				observedHolderSharesThisOwner: observedHolderOwnerId === ownerId,
				observedHolderIsThisLease: record.ownerId === durableOwnerId
			};
		} catch (error) {
			return { observedHolder: "unreadable", ...uploadFailureLogDetails(error) };
		}
	};
	const refuseClaim = (
		condition: string,
		session: FileUploadSession,
		observedSession: FileUploadSession | null = session,
		invalidOwner?: { lengthField: "snapshotOwnerIdLength" | "persistedOwnerIdLength"; value: unknown },
		extra?: Record<string, unknown>
	) => {
		try {
			// NOTE: the message string is pinned by six assertions across four spec files
			// (adminUploadLease.cas-and-identity, adminUploadAdapters, fileUploadOwnerIdentityValidation,
			// breakit-round1.lease-identity-boundaries), so the condition stays in the payload rather
			// than being folded into the message. A copied DevTools log collapses this payload to the
			// literal "Object" — expand it, or read the sibling `owns()` / renewal warnings added
			// alongside this one, which carry their distinguishing reason in the message itself.
			adminUploadTelemetry.logWarn("[Upload] upload lease claim refused", {
				// Keep claim and context metadata from the same observation; the caller may be stale.
				...diagnostics.describe(observedSession ?? {}, ownerClaimId, purpose),
				condition,
				sessionId: session.id,
				uploadId: session.uploadId,
				...(isValidFileUploadOwnerId(ownerId) ? { ownerId } : { localOwnerIdLength: getFileUploadOwnerIdLength(ownerId) }),
				...(invalidOwner && !isValidFileUploadOwnerId(invalidOwner.value)
					? { [invalidOwner.lengthField]: getFileUploadOwnerIdLength(invalidOwner.value) }
					: {}),
				leaseClaimId: ownerClaimId,
				...extra
			});
		} catch {
			// A diagnostic failure must not turn a refused lease claim into a rejected operation.
		}
		return false;
	};
	return {
		ownerClaimId,
		getDurableToken: (sessionId) => durableTokens.get(sessionId),
		releaseClaim,
		release: (session) => releaseClaim(session),
		async claim(session) {
			const expectedOwnerClaimId = typeof expectedOwnerClaim === "function" ? expectedOwnerClaim() : expectedOwnerClaim;
			if (!isValidFileUploadOwnerId(ownerId)) return refuseClaim("invalid-local-owner-id", session);
			if (session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) {
				return refuseClaim("invalid-snapshot-owner-id", session, undefined, {
					lengthField: "snapshotOwnerIdLength",
					value: session.uploadOwnerId
				});
			}
			// LSP-19537: refuse BEFORE acquireWriter, so a refused claim never takes a writer epoch
			// it will just have to release. This is the fence; discoverPending's filter (Task 5) is
			// only the presentation-layer skip that keeps a live foreign lane's row out of the
			// candidate list in the first place — this is what makes the fence authoritative even if
			// that filter is ever bypassed or stale.
			const laneIntentLiveness = await getFileUploadLaneIntentLiveness(session, Date.now());
			if (laneIntentLiveness === "alive")
				return refuseClaim("lane-intent-held", session, null, undefined, {
					observedLaneIntentId: isValidFileUploadOwnerId(session.laneIntentId) ? session.laneIntentId : undefined
				});
			const isForeignLaneTakeover =
				session.laneIntentId !== undefined && isValidFileUploadOwnerId(session.laneIntentId) && laneIntentLiveness !== "own";
			await ensureAdminUploadWriterAdmission();
			let durableOwnership: DurableOwnershipPort | undefined;
			try {
				durableOwnership = await getDurableOwnership();
			} catch (error) {
				console.warn("[Upload] Durable upload ownership unavailable; refusing upload claim", uploadFailureLogDetails(error));
				return false;
			}
			const durableToken = durableOwnership
				? await durableOwnership.acquireWriter({
						originKey: ADMIN_UPLOAD_ORIGIN_KEY,
						identity: { itemId: session.id },
						ownerId: durableOwnerId,
						ttlMs: ADMIN_WRITER_LEASE_TTL_MS
					})
				: undefined;
			if (durableOwnership && !durableToken) {
				return refuseClaim("durable-writer-held", session, undefined, undefined, await describeHeldWriterLease(session));
			}
			let observedSession: FileUploadSession | undefined;
			try {
				observedSession = await getFileUploadSessionByUploadId(session.uploadId);
			} catch (error) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				throw error;
			}
			if (observedSession && observedSession.id !== session.id) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				return refuseClaim("upload-id-session-mismatch", session, observedSession);
			}
			if (observedSession?.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(observedSession.uploadOwnerId)) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				return refuseClaim("invalid-persisted-owner-id", session, observedSession, {
					lengthField: "persistedOwnerIdLength",
					value: observedSession.uploadOwnerId
				});
			}
			// A cancelled row is a durable tombstone, not a lease that can be resumed from an
			// earlier in-memory snapshot. The old snapshot still carries this lease's claim id;
			// allowing it back in would resurrect a user cancellation. A deliberate replacement
			// must enter with a fresh, ownerless identity (the tombstone's owner fields are cleared
			// atomically), which is the only same-tab cancellation re-entry this API supports.
			const reclaimsObservedOwnCancellation =
				observedSession?.status === "cancelled" &&
				session.status === "cancelled" &&
				typeof session.uploadCancellationGeneration === "number" &&
				session.uploadOwnerId === undefined &&
				session.uploadOwnerHeartbeatAt === undefined &&
				session.uploadOwnerClaimId === undefined &&
				session.uploadCancellationGeneration === observedSession.uploadCancellationGeneration;
			const observedLeaseWasAlreadyThisLease =
				observedSession?.uploadOwnerId === ownerId && observedSession.uploadOwnerClaimId === ownerClaimId;
			const observedLeaseAllowsDirectReentry =
				observedLeaseWasAlreadyThisLease ||
				(observedSession?.uploadOwnerId === ownerId &&
					expectedOwnerClaimId !== undefined &&
					observedSession.uploadOwnerClaimId === expectedOwnerClaimId);
			const claimsForeignOwner = Boolean(observedSession?.uploadOwnerId && !observedLeaseAllowsDirectReentry);
			if (observedSession?.status === "complete" || (observedSession?.status === "cancelled" && !reclaimsObservedOwnCancellation)) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				return refuseClaim("terminal-session", session, observedSession);
			}
			if (observedSession?.uploadOwnerId && !observedLeaseAllowsDirectReentry) {
				let competingOwnerIsLive: boolean;
				try {
					competingOwnerIsLive = (await getCompetingFileUploadOwnerLiveness(observedSession)) !== "dead";
				} catch (error) {
					if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
					throw error;
				}
				if (competingOwnerIsLive) {
					if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
					return refuseClaim("competing-owner-live", session, observedSession);
				}
			}

			let markedForeignOwnerClaim = false;
			const commitClaim = () =>
				withIdbReopen(() =>
					db.transaction("rw", fileUploadSessionControlTables, async () => {
						const persistedSession = await readFileUploadSessionByUploadId(session.uploadId);
						if (!persistedSession || persistedSession.id !== session.id) {
							refuseClaim("persisted-session-missing-or-replaced", session, persistedSession ?? null);
							return undefined;
						}
						if (persistedSession.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(persistedSession.uploadOwnerId)) {
							refuseClaim("invalid-persisted-owner-id", session, persistedSession, {
								lengthField: "persistedOwnerIdLength",
								value: persistedSession.uploadOwnerId
							});
							return undefined;
						}
						const reclaimsPersistedCancellation =
							persistedSession.status === "cancelled" &&
							session.status === "cancelled" &&
							typeof session.uploadCancellationGeneration === "number" &&
							session.uploadOwnerId === undefined &&
							session.uploadOwnerHeartbeatAt === undefined &&
							session.uploadOwnerClaimId === undefined &&
							session.uploadCancellationGeneration === persistedSession.uploadCancellationGeneration;
						if (
							persistedSession.status === "complete" ||
							(persistedSession.status === "cancelled" && !reclaimsPersistedCancellation)
						) {
							refuseClaim("persisted-session-terminal", session, persistedSession);
							return undefined;
						}

						const observedLeaseIsUnchanged =
							observedSession !== undefined &&
							persistedSession.id === observedSession.id &&
							persistedSession.uploadId === observedSession.uploadId &&
							persistedSession.status === observedSession.status &&
							persistedSession.uploadOwnerId === observedSession.uploadOwnerId &&
							persistedSession.uploadOwnerHeartbeatAt === observedSession.uploadOwnerHeartbeatAt &&
							persistedSession.uploadCancellationGeneration === observedSession.uploadCancellationGeneration &&
							// A corrupt claim id is reachable independently of a corrupt owner, and IndexedDB
							// re-clones every read, so `===` on a non-primitive claim is never true and an
							// ownerless row with such a claim could never be claimed (ls-heroku-ci review). Both
							// owner-id checks above already refuse a non-primitive OWNER before this CAS, so the
							// claim cell is the only one that needs this; it is identity for `undefined` and for
							// every string, so a real successor's string claim still compares unequal.
							toComparableUploadOwnerCell(persistedSession.uploadOwnerClaimId) ===
								toComparableUploadOwnerCell(observedSession.uploadOwnerClaimId) &&
							// LSP-19537: refuse (lease-changed-before-commit) an intent that appeared or moved
							// between observe and commit — the same CAS discipline as the claim id above.
							toComparableUploadOwnerCell(persistedSession.laneIntentId) ===
								toComparableUploadOwnerCell(observedSession.laneIntentId);
						const alreadyOwnedByThisLease =
							persistedSession.uploadOwnerId === ownerId && persistedSession.uploadOwnerClaimId === ownerClaimId;
						if (!observedLeaseIsUnchanged && !alreadyOwnedByThisLease && !reclaimsPersistedCancellation) {
							refuseClaim("lease-changed-before-commit", session, persistedSession);
							return undefined;
						}

						const ownerFields = {
							...getFileUploadSessionOwnerFields(ownerId, ownerClaimId),
							...diagnostics.ownerFields(purpose)
						};
						ownerFields.uploadOwnerHeartbeatAt = Math.max(
							ownerFields.uploadOwnerHeartbeatAt ?? 0,
							(persistedSession.uploadOwnerHeartbeatAt ?? 0) + 1
						);
						// Consume the cancellation generation when deliberately replacing its tombstone.
						// A pre-fence bundle that later cancels by spreading this row will preserve the new
						// generation, so no snapshot from before this replacement can reclaim that cancellation.
						const replacementCancellationGeneration = reclaimsPersistedCancellation
							? nextFileUploadCancellationGeneration(persistedSession)
							: undefined;
						await updateFileUploadSession(persistedSession.id, {
							status: "uploading",
							lastError: undefined,
							...ownerFields,
							...(replacementCancellationGeneration !== undefined
								? { uploadCancellationGeneration: replacementCancellationGeneration }
								: {}),
							...(isForeignLaneTakeover ? { laneIntentId: undefined, laneIntentAt: undefined } : {})
						});
						Object.assign(session, {
							status: "uploading",
							lastError: undefined,
							...ownerFields,
							...(replacementCancellationGeneration !== undefined
								? { uploadCancellationGeneration: replacementCancellationGeneration }
								: {}),
							...(isForeignLaneTakeover ? { laneIntentId: undefined, laneIntentAt: undefined } : {})
						});
						if (claimsForeignOwner && !markedForeignOwnerClaim) {
							markCurrentPageInitiatedFileUploadSession(session);
							markedForeignOwnerClaim = true;
						}
						return ownerFields;
					})
				);
			let claimedOwnerFields: ReturnType<typeof getFileUploadSessionOwnerFields> | undefined;
			try {
				const claimEffect = () =>
					claimsForeignOwner ? runWhileUploadOwnerLockIsFree(observedSession!.uploadOwnerId!, commitClaim) : commitClaim();
				if (durableOwnership && durableToken) {
					const fenced = await durableOwnership.commitIfWriterOwns({
						token: durableToken,
						storeNames: ADMIN_FENCED_UPLOAD_STORES,
						effect: claimEffect
					});
					claimedOwnerFields = fenced.kind === "applied" ? fenced.value : undefined;
				} else {
					claimedOwnerFields = await claimEffect();
				}
			} catch (error) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				if (markedForeignOwnerClaim) forgetCurrentPageInitiatedFileUploadSession(session);
				throw error;
			}
			if (!claimedOwnerFields) {
				if (durableOwnership && durableToken) await durableOwnership.releaseWriter(durableToken);
				if (markedForeignOwnerClaim) forgetCurrentPageInitiatedFileUploadSession(session);
				// The earlier read is no longer current after a CAS refusal. Detailed refusals above
				// carry their actual observation; this summary must not re-label the stale snapshot.
				return refuseClaim("claim-cas-rejected", session, null);
			}
			if (durableToken) durableTokens.set(session.id, durableToken);
			Object.assign(session, { status: "uploading", ...claimedOwnerFields });
			// A freshly created active row was already admitted atomically with its insert. Do not
			// double-count it when the same lease performs the immediate claim.
			if (!claimsForeignOwner && !observedLeaseWasAlreadyThisLease) markCurrentPageInitiatedFileUploadSession(session);
			admittedSessionUploadIds.set(session.id, session.uploadId);
			adminUploadTelemetry.logInfo("recording.fileUpload.leaseClaimed", {
				...describe(session),
				sessionId: session.id,
				epoch: durableToken?.epoch,
				leaseAgeMs: durableToken ? Date.now() - durableToken.acquiredAt : undefined
			});
			for (const listener of claimListeners) {
				try {
					listener(session);
				} catch (error) {
					console.warn("[Upload] upload lease claim listener failed", uploadFailureLogDetails(error));
				}
			}
			return true;
		},
		touch() {
			return (touchInFlight ??= performTouch().finally(() => {
				touchInFlight = undefined;
			}));
		},
		async owns(session) {
			// Ownership is refused for an unusable identity exactly like `claim()` does, and says which
			// side was unusable — an unlogged `false` here is indistinguishable from ordinary
			// non-ownership and sends the next triage looking for a competing tab that does not exist.
			const refuseOwnership = (
				condition: string,
				observedOwnerId: unknown,
				observedSession: FileUploadSession | null = session
			): false => {
				try {
					adminUploadTelemetry.logWarn("[Upload] upload lease ownership refused", {
						...diagnostics.describe(observedSession ?? {}, ownerClaimId, purpose),
						condition,
						sessionId: session.id,
						uploadId: session.uploadId,
						// Same rule as refuseClaim: a well-formed owner id is safe to name, a corrupt one is
						// reported only by length.
						...(isValidFileUploadOwnerId(observedOwnerId)
							? { observedOwnerId }
							: { observedOwnerIdLength: getFileUploadOwnerIdLength(observedOwnerId) }),
						leaseClaimId: ownerClaimId
					});
				} catch {
					// A diagnostic failure must not turn a refused ownership check into a rejected operation.
				}
				return false;
			};
			if (!isValidFileUploadOwnerId(ownerId)) return refuseOwnership("invalid-local-owner-id", ownerId);
			// An ownerless row is a legacy case, not corruption — `claim()` and the reconciler gate both
			// treat it that way, so it must not be logged as an invalid identity on the resume path.
			if (session.uploadOwnerId === undefined) {
				// Not corruption (a legacy ownerless row is legitimate), so this stays out of
				// `refuseOwnership`'s invalid-identity wording — but it must not stay silent either: it is
				// one of only two unlogged `false`s in a function whose every other exit is loud, and the
				// chunk loop turns this `false` straight into a traceless `yielded`.
				adminUploadTelemetry.logWarn("[Upload] upload lease ownership refused: ownerless-session-row", {
					...describe(session),
					sessionId: session.id,
					uploadId: session.uploadId
				});
				return false;
			}
			if (!isValidFileUploadOwnerId(session.uploadOwnerId))
				return refuseOwnership("invalid-snapshot-owner-id", session.uploadOwnerId);
			await ensureAdminUploadWriterAdmission();
			const token = durableTokens.get(session.id);
			const durableOwnership = await getDurableOwnership();
			if (durableOwnership && (!token || !(await durableOwnership.ownsWriter(token)))) {
				// The second of the two unlogged `false`s. Distinguish the two causes: "this scope never
				// had / already dropped a token" is a local bookkeeping fact (see the renewal branch in
				// touch()), whereas "the durable record has moved on" means another scope genuinely owns
				// the writer. They demand opposite fixes and used to be indistinguishable from the outside.
				adminUploadTelemetry.logWarn("[Upload] upload lease ownership refused: durable-writer-not-held", {
					...describe(session),
					sessionId: session.id,
					uploadId: session.uploadId,
					cause: token ? "record-no-longer-matches-token" : "no-token-for-this-scope",
					epoch: token?.epoch
				});
				return false;
			}
			const persisted = await getFileUploadSessionByUploadId(session.uploadId);
			if (
				persisted?.id === session.id &&
				persisted.uploadOwnerId !== undefined &&
				!isValidFileUploadOwnerId(persisted.uploadOwnerId)
			) {
				return refuseOwnership("invalid-persisted-owner-id", persisted.uploadOwnerId, persisted);
			}
			// A genuine competing claim is the case triage most needs to see, so it cannot be the one
			// unlogged branch between two loud ones. Named like claim()'s conditions.
			if (persisted?.id !== session.id)
				return refuseOwnership("persisted-session-missing-or-replaced", persisted?.uploadOwnerId, persisted ?? null);
			if (persisted.uploadOwnerId !== ownerId) return refuseOwnership("persisted-owner-mismatch", persisted.uploadOwnerId, persisted);
			if (session.uploadOwnerId !== ownerId) return refuseOwnership("snapshot-owner-mismatch", session.uploadOwnerId);
			const ownsClaim = persisted.uploadOwnerClaimId === ownerClaimId && session.uploadOwnerClaimId === ownerClaimId;
			if (!ownsClaim) {
				try {
					adminUploadTelemetry.logWarn("[Upload] upload lease ownership claim mismatch", {
						...describe(persisted),
						sessionId: session.id,
						uploadId: session.uploadId,
						observedClaimId: isValidFileUploadOwnerId(persisted.uploadOwnerClaimId) ? persisted.uploadOwnerClaimId : undefined,
						leaseClaimId: ownerClaimId
					});
				} catch {
					// A diagnostic failure must not turn a refused lease ownership check into a rejected operation.
				}
			}
			return ownsClaim;
		},
		async commitIfOwned(session, effect) {
			await ensureAdminUploadWriterAdmission();
			const token = durableTokens.get(session.id);
			const durableOwnership = await getDurableOwnership();
			// TRANSITIONAL — reachable only via getDurableOwnership's MODE sniff (see its comment).
			// The legacy row-CAS harness disables the shared durable coordinator in unit mode, and
			// this branch preserves that path's ownership check: the concrete effect still performs
			// its own uploadId/owner/claim CAS rather than the durable writer fence. That is a weaker
			// guard than the fenced commit below, so this is not a safe general fallback — it exists
			// because the seam has no typed "durable ownership unavailable" state to branch on.
			// Scheduled for deletion by the registered "admin upload ownership seam" follow-up.
			if (!durableOwnership) {
				if (!(await this.owns(session))) return { kind: "skipped-not-owned" };
				return { kind: "applied", value: await effect() };
			}
			if (!token) return { kind: "skipped-not-owned" };
			return durableOwnership.commitIfWriterOwns({
				token,
				// Include every app store a post-await upload checkpoint can touch.
				// Nested Dexie writes from the concrete session store join this tx.
				storeNames: ADMIN_FENCED_UPLOAD_STORES,
				effect: async () => {
					const current = await readFileUploadSession(session.id);
					if (
						!current ||
						current.uploadId !== session.uploadId ||
						current.uploadOwnerId !== ownerId ||
						current.uploadOwnerClaimId !== ownerClaimId
					) {
						throw new Error("Admin upload writer identity changed before fenced commit");
					}
					return effect();
				}
			});
		},
		ownerLiveness: getFileUploadOwnerLiveness
	};
}
